import { useTranslation } from 'react-i18next'

import type { InviteState, UserStatus } from '../types'
import { STATUS_PALETTE } from '../utils'

type Props = {
  status: UserStatus
  /**
   * When the member is `invited`, the invite lifecycle state refines the pill:
   * pending → warning "Invite pending", expired → neutral-warning "Invite
   * expired", revoked → error "Invite revoked".
   */
  inviteState?: InviteState | null
  size?: 'sm' | 'md'
}

// Expired/revoked invites get their own colour so they read differently from a
// healthy pending invite.
const INVITE_OVERRIDE: Partial<
  Record<InviteState, { bg: string; color: string; dot: string }>
> = {
  expired: {
    bg: 'var(--ant-color-fill-secondary)',
    color: 'var(--ant-color-text-secondary)',
    dot: 'var(--ant-color-warning)'
  },
  revoked: {
    bg: 'var(--ant-color-error-bg)',
    color: 'var(--ant-color-error)',
    dot: 'var(--ant-color-error)'
  }
}

export const UsersStatusPill = ({
  status,
  inviteState,
  size = 'md'
}: Props) => {
  const { t } = useTranslation()
  const tiny = size === 'sm'

  // Refine the "invited" pill with the invite lifecycle state when known.
  const useInvite =
    status === 'invited' &&
    (inviteState === 'expired' || inviteState === 'revoked')
  const s = useInvite
    ? (INVITE_OVERRIDE[inviteState as InviteState] ?? STATUS_PALETTE[status])
    : STATUS_PALETTE[status]
  const label =
    status === 'invited' && inviteState
      ? t(`users.lifecycle.inviteState.${inviteState}`)
      : t(`users.statuses.${status}`)

  return (
    <span
      style={{
        display: 'inline-flex',
        alignItems: 'center',
        gap: 5,
        padding: tiny ? '0 6px' : '1px 8px',
        background: s.bg,
        color: s.color,
        borderRadius: 999,
        fontSize: tiny ? 10 : 11,
        fontWeight: 500,
        lineHeight: 1.55,
        whiteSpace: 'nowrap'
      }}
    >
      <span
        style={{
          width: 6,
          height: 6,
          borderRadius: '50%',
          background: s.dot
        }}
      />
      {label}
    </span>
  )
}
